import { useState } from "react";
import { Keyboard } from "react-native";

import { useUser } from "@clerk/clerk-expo";
import { CustomSnackbar, Loader } from "@memorang/ui";
import { useQuery } from "@tanstack/react-query";
import { Redirect, useRouter } from "expo-router";
import { useLocalSearchParams } from "expo-router";
import { useTranslation } from "react-i18next";
import EmailView from "../components/EmailView";
import DialogContactSupport from "../components/dialogs/DialogContactSupport";
import useClerkAuth from "../hooks/useClerkAuth";
import {
	formatError,
	isIncorrectPasscodeError,
	isIncorrectPasswordError,
	isSignUpError,
	isTestModeEmail,
} from "./helpers";

type Props = {
	companyName: string;
	buildType: string;
	currentAppVersion: string;
	redirectUrl: string;
	handleTracking: (data: Record<string, string>) => void;
	supportEmail: string;
	handleNewUserSignUp: () => void;
	emailFromUrl?: string;
	lastLoggedInEmail?: string;
	appId: string;
	tos?: string;
	privacy?: string;
	tenantId: string;
	onError?: (message: string) => void;
	logError: (error: unknown, title: string) => void;
};

const EmailContainer = ({
	companyName,
	handleTracking,
	supportEmail,
	handleNewUserSignUp,
	buildType,
	currentAppVersion,
	redirectUrl,
	emailFromUrl,
	lastLoggedInEmail,
	appId,
	tenantId,
	tos,
	privacy,
	onError,
	logError,
}: Props) => {
	const router = useRouter();
	const [showContactSupportDialog, setShowContactSupportDialog] =
		useState(false);
	const [showInvalidDomainSnackBar, setShowInvalidDomainSnackBar] =
		useState(false);
	const [showPasswordField, setShowPasswordField] = useState(false);

	const { t } = useTranslation();

	const { user, isSignedIn } = useUser();
	const { redeemCode } = useLocalSearchParams<{ redeemCode: string }>();
	const [email, setEmail] = useState<string | undefined>(undefined);
	const [isTestModeAuthInProgress, setIsTestModeAuthInProgress] =
		useState(false);
	const userLogicalId = user?.publicMetadata.logicalId as string;

	useQuery({
		queryKey: ["verify-email", email],
		queryFn: async () => {
			const newUser = await user?.reload();
			return newUser ?? null;
		},
		enabled: !userLogicalId && isTestModeAuthInProgress,
		refetchInterval: 500,
	});

	const { authenticate, isLoading, isAuthInProgress } = useClerkAuth({
		appId,
		tenantId,
		redirectUrl,
		onEmailLinkVerify: (email: string) => {
			router.push(
				`/(auth)/verify?email=${email}${redeemCode ? `&redeemCode=${redeemCode}` : ""}`,
			);
		},
		onEmailCodeSuccess: () => {
			router.push("/home");
		},
		handleNewUserSignUp,
	});

	if (isLoading) {
		return <Loader />;
	}

	const onChangeEmail = (email: string) => {
		setShowPasswordField(isTestModeEmail(email));
	};

	const handleSubmit = async (email: string, password?: string) => {
		Keyboard.dismiss();
		setEmail(email);
		setIsTestModeAuthInProgress(isTestModeEmail(email));
		try {
			await authenticate(email, password);
		} catch (error: unknown) {
			setIsTestModeAuthInProgress(false);
			if (isIncorrectPasswordError(error)) {
				onError?.(t("auth.incorrectPassword"));
			} else if (isIncorrectPasscodeError(error)) {
				onError?.(t("auth.incorrectPasscode"));
			} else {
				const isSignUp = isSignUpError(error);
				const longMessage = formatError(error);
				logError(
					longMessage,
					`Error during ${password ? "test mode " : ""}${isSignUp ? "sign-up" : "sign-in"}`,
				);
				setShowContactSupportDialog(true);
				handleTracking({
					eventName: isSignUp ? "mem_signup_attempted" : "mem_login_attempted",
					email,
				});
			}
		} finally {
			setIsTestModeAuthInProgress(false);
		}
	};

	if (isSignedIn && userLogicalId) {
		return <Redirect href="/home" />;
	}

	return (
		<>
			<EmailView
				handleSubmit={handleSubmit}
				loading={isAuthInProgress || isTestModeAuthInProgress}
				emailFromUrl={emailFromUrl}
				lastLoggedInEmail={lastLoggedInEmail}
				tos={tos}
				privacy={privacy}
				showPasswordField={showPasswordField}
				onChangeEmail={onChangeEmail}
			/>
			{showContactSupportDialog && (
				<DialogContactSupport
					visible={showContactSupportDialog}
					companyName={companyName}
					supportEmail={supportEmail}
					buildType={buildType}
					currentAppVersion={currentAppVersion}
					handleDismiss={() => setShowContactSupportDialog(false)}
				/>
			)}
			{showInvalidDomainSnackBar && (
				<CustomSnackbar
					handleDismiss={() => setShowInvalidDomainSnackBar(false)}
					visible={showInvalidDomainSnackBar}
					duration={2000}
					message="You must use a permanent email address"
				/>
			)}
		</>
	);
};

export default EmailContainer;
